import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { Vendeur } from '@troc/shared'
import { api, definirJeton, evenements, jetonCourant } from '../api'

interface ValeurSession {
  vendeur: Vendeur | null
  chargement: boolean
  connecter: (code: string, pin: string) => Promise<void>
  deconnecter: () => Promise<void>
}

const Contexte = createContext<ValeurSession | null>(null)

export function FournisseurSession({ children }: { children: ReactNode }) {
  const [vendeur, setVendeur] = useState<Vendeur | null>(null)
  const [chargement, setChargement] = useState(true)

  // Un jeton en mémoire locale suffit à rouvrir la caisse après un
  // redémarrage de l'iPad — sans quoi il faudrait retaper le PIN chaque matin.
  useEffect(() => {
    let vivant = true
    if (!jetonCourant()) {
      setChargement(false)
      return
    }

    /**
     * Le serveur ne répond pas toujours. Le NAS peut être occupé à autre chose,
     * le tailnet peut mettre du temps à s'établir au réveil de l'iPad — et sans
     * garde-fou, l'écran reste sur « Ouverture de la caisse… » indéfiniment,
     * sans rien proposer. Passé huit secondes, on rend la main : l'écran de
     * connexion s'affiche, et retaper son PIN suffit à repartir.
     */
    const abandon = window.setTimeout(() => {
      if (vivant) setChargement(false)
    }, 8_000)

    api<{ vendeur: Vendeur }>('/session')
      .then((r) => vivant && setVendeur(r.vendeur))
      .catch(() => undefined)
      .finally(() => {
        window.clearTimeout(abandon)
        if (vivant) setChargement(false)
      })
    return () => {
      vivant = false
      window.clearTimeout(abandon)
    }
  }, [])

  useEffect(() => {
    const surExpiration = () => setVendeur(null)
    evenements.addEventListener('session-expiree', surExpiration)
    return () => evenements.removeEventListener('session-expiree', surExpiration)
  }, [])

  const connecter = useCallback(async (code: string, pin: string) => {
    const reponse = await api<{ token: string; vendeur: Vendeur }>('/session', {
      methode: 'POST',
      corps: { code, pin },
    })
    definirJeton(reponse.token)
    setVendeur(reponse.vendeur)
  }, [])

  const deconnecter = useCallback(async () => {
    try {
      await api('/session', { methode: 'DELETE' })
    } catch {
      // Le serveur peut être injoignable : on ferme quand même côté iPad.
    }
    definirJeton(null)
    setVendeur(null)
  }, [])

  const valeur = useMemo(
    () => ({ vendeur, chargement, connecter, deconnecter }),
    [vendeur, chargement, connecter, deconnecter],
  )

  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>
}

export function useSession(): ValeurSession {
  const valeur = useContext(Contexte)
  if (!valeur) throw new Error('useSession hors de FournisseurSession')
  return valeur
}
